import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Heart, Shield, MessageCircle, Home, Clipboard, UserCheck, BookOpen, Settings, Leaf, TrendingUp, Sparkles } from 'lucide-react';

// Couleurs pour le BAC ASSP - thème accompagnement / social
const theme = {
  primary: '#7C3AED',      // Violet
  secondary: '#EC4899',    // Rose
  accent1: '#8B5CF6',      // Violet clair (BC1)
  accent2: '#06B6D4',      // Cyan (BC2)
  accent3: '#F59E0B',      // Orange (BC3)
  accent4: '#10B981',      // Vert (BC4)
  accent5: '#3B82F6',      // Bleu (BC7)
  accent6: '#EF4444',      // Rouge (BC8)
  background: '#FAF5FF',   // Fond violet très clair
  text: '#1E293B',
  success: '#10B981',
  error: '#EF4444',
};

const slides = [
  // INTRODUCTION
  {
    id: 1,
    title: "Réussir sa VAE BAC ASSP",
    subtitle: "Baccalauréat Professionnel Accompagnement, Soins et Services à la Personne",
    description: "Les étapes clés pour réussir votre validation",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE BAC ASSP ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience dans l'accompagnement des personnes",
      "Obtenir un diplôme de niveau 4 (Baccalauréat) reconnu par l'État",
      "Travailler auprès de personnes en situation de dépendance",
      "Accéder à des postes en structure ou à domicile",
      "Poursuivre vers des études supérieures (BTS, IFSI...)"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé (1607h cumulées)",
      "Activités en lien avec l'accompagnement et les soins",
      "Expérience auprès de personnes âgées, en situation de handicap ou enfants",
      "Travail en structure (EHPAD, crèche, hôpital) ou à domicile"
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Validation par blocs",
    type: "info-bloc",
    text: "Chaque bloc de compétences constitue une partie de votre diplôme qui peut être validée indépendamment des autres et reste acquise à vie."
  },
  {
    id: 6,
    title: "Les 4 blocs de compétences",
    type: "blocs-overview",
    blocs: [
      { num: 1, title: "Accompagner la personne dans une approche globale et individualisée", color: theme.accent1 },
      { num: 2, title: "Réaliser des soins d'hygiène, de confort et de sécurité", color: theme.accent2 },
      { num: 3, title: "Travailler et communiquer en équipe pluriprofessionnelle", color: theme.accent3 },
      { num: 4, title: "Réaliser des actions d'éducation à la santé", color: theme.accent4 },
    ]
  },

  // BLOC 1 - Accompagner la personne
  {
    id: 7,
    title: "Bloc 1 – Accompagner la personne",
    subtitle: "dans une approche globale et individualisée",
    type: "bloc-title",
    color: theme.accent1,
    icon: "heart",
    code: "RNCP37231BC01"
  },
  {
    id: 8,
    title: "BC1 – Compétences attendues",
    type: "competence",
    color: theme.accent1,
    items: [
      "Adopter une posture professionnelle adaptée",
      "Accueillir, communiquer avec la personne, sa famille, son entourage",
      "Participer à la conception, au suivi, à la mise en œuvre et à l'évaluation du projet individualisé, du projet de vie, en lien avec l'équipe pluriprofessionnelle",
      "Concevoir et mettre en œuvre des activités d'acquisition ou de maintien de l'autonomie et de la vie sociale, pour une personne ou un groupe"
    ]
  },

  // BLOC 2 - Soins d'hygiène
  {
    id: 9,
    title: "Bloc 2 – Intervenir lors des soins",
    subtitle: "d'hygiène, de confort et de sécurité, dans les activités de la vie quotidienne",
    type: "bloc-title",
    color: theme.accent2,
    icon: "shield",
    code: "RNCP37231BC02"
  },
  {
    id: 10,
    title: "BC2 – Compétences attendues",
    type: "competence",
    color: theme.accent2,
    items: [
      "Réaliser les activités liées à l'hygiène, au confort de la personne et à la sécurisation",
      "Surveiller l'état de santé de la personne et intervenir en conséquence",
      "Assurer l'hygiène de l'environnement proche de la personne et veiller au bon état de fonctionnement du lit, des aides techniques, des dispositifs médicaux",
      "Distribuer des repas équilibrés conformes aux besoins de la personne, installer la personne et accompagner la prise des repas"
    ]
  },

  // BLOC 3 - Travail en équipe
  {
    id: 11,
    title: "Bloc 3 – Travailler et communiquer",
    subtitle: "en équipe pluriprofessionnelle",
    type: "bloc-title",
    color: theme.accent3,
    icon: "users",
    code: "RNCP37231BC03"
  },
  {
    id: 12,
    title: "BC3 – Compétences attendues",
    type: "competence",
    color: theme.accent3,
    items: [
      "Gérer ses activités en interagissant avec l'équipe pluriprofessionnelle dans une posture professionnelle adaptée",
      "Traiter et transmettre des informations en intégrant les différents outils numériques",
      "Participer à la démarche qualité et à la prévention des risques professionnels",
      "Coordonner et conduire une équipe de bionettoyage",
      "Participer à l'accueil, à l'encadrement et à la formation de stagiaires, à l'accueil des nouveaux agents, des bénévoles"
    ]
  },

  // BLOC 4 - Éducation à la santé
  {
    id: 13,
    title: "Bloc 4 – Réaliser des actions",
    subtitle: "d'éducation à la santé pour un public ciblé",
    type: "bloc-title",
    color: theme.accent4,
    icon: "book",
    code: "RNCP37231BC04"
  },
  {
    id: 14,
    title: "BC4 – Compétences attendues",
    type: "competence",
    color: theme.accent4,
    items: [
      "Analyser les besoins du public",
      "Concevoir une action d'éducation à la santé",
      "Mettre en œuvre et évaluer l'action d'éducation à la santé"
    ]
  },

  // CONSEILS ET ORAL
  {
    id: 15,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations d'accompagnement concrètes",
      "Analyser vos pratiques professionnelles",
      "Justifier vos choix et vos actions",
      "Montrer votre posture professionnelle"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je m'occupe des personnes âgées »",
      right: "« J'accompagne Mme Martin dans sa toilette en respectant son autonomie. J'observe son état général et je transmets à l'équipe les informations pertinentes pour adapter le projet de soins. »"
    }
  },
  {
    id: 16,
    title: "L'oral devant le jury",
    type: "oral",
    details: [
      { label: "Durée totale", value: "1 heure" },
      { label: "Présentation personnelle", value: "10 minutes" },
      { label: "Échanges avec le jury", value: "50 minutes" }
    ],
    note: "Questions sur vos situations d'accompagnement, votre positionnement professionnel et votre connaissance des blocs de compétences."
  },
  {
    id: 17,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre BAC Pro ASSP !" },
      { status: "partial", text: "Validation partielle", desc: "Blocs de compétences acquis à vie, reste à valider les autres" },
      { status: "retry", text: "Ajournement", desc: "Possibilité de représenter le dossier après travail complémentaire" }
    ]
  },
  {
    id: 18,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Aide-soignant(e) (passerelle)",
      "Accompagnant(e) éducatif petite enfance",
      "Assistant(e) de vie aux familles",
      "Agent de service en EHPAD",
      "Auxiliaire de vie sociale",
      "Poursuite en BTS SP3S ou ESF"
    ]
  },
  {
    id: 19,
    title: "En résumé",
    type: "summary",
    points: [
      "Le BAC ASSP valorise vos compétences d'accompagnement",
      "Chaque bloc validé est acquis à vie",
      "Une préparation méthodique favorise la réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'heart': return <Heart size={size} />;
    case 'shield': return <Shield size={size} />;
    case 'users': return <Users size={size} />;
    case 'book': return <BookOpen size={size} />;
    case 'trending': return <TrendingUp size={size} />;
    case 'leaf': return <Leaf size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 2rem', border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <GraduationCap size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1rem', opacity: 0.85 }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.success}`
              }}>
                <CheckCircle size={22} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', maxWidth: '750px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "info-bloc":
      return (
        <div style={{ padding: '2rem' }}>
          <div style={{
            background: `linear-gradient(135deg, ${theme.accent1}15 0%, ${theme.accent4}15 100%)`,
            borderRadius: '1rem',
            padding: '2rem',
            textAlign: 'center',
            border: `2px solid ${theme.primary}30`
          }}>
            <Sparkles size={50} style={{ color: theme.primary, marginBottom: '1rem' }} />
            <p style={{ fontSize: '1.2rem', color: theme.text, lineHeight: '1.8', maxWidth: '600px', margin: '0 auto' }}>
              {slide.text}
            </p>
          </div>
        </div>
      );

    case "blocs-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', maxWidth: '850px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: bloc.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.1rem', flexShrink: 0
                }}>BC{bloc.num}</div>
                <h4 style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{bloc.title}</h4>
              </div>
            ))}
          </div>
        </div>
      );

    case "bloc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.5rem' }}>{slide.code}</p>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "competence":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '1rem 1.5rem', 
            borderRadius: '0.75rem 0.75rem 0 0', marginBottom: '0'
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold' }}>{slide.title}</h2>
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0',
                borderBottom: idx < slide.items.length - 1 ? `1px solid #eee` : 'none'
              }}>
                <CheckCircle size={18} style={{ color: slide.color, flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: '0.9rem', color: theme.text, lineHeight: '1.5' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '650px', margin: '0 auto', padding: '1rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.primary}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: theme.primary, textAlign: 'center', fontSize: '0.95rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.9rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.8rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.75rem', background: theme.primary + '15', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <GraduationCap size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '550px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={20} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '0.95rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold' }}>
              Lancez-vous dans l'aventure VAE BAC ASSP avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const BACASSPPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="bac-assp-presentation">
      <div style={{ minHeight: '450px', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.primary}20`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: `1px solid ${theme.primary}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500' }} data-testid="prev-slide-btn">
          <ChevronLeft size={18} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={16} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.95rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', padding: '0.75rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '8px', height: '8px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.secondary, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.4 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default BACASSPPresentation;
